import { AuthState } from '../types';

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'admin123';
const AUTH_KEY = 'puc_admin_auth';
const SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours

class AuthService {
  // Check if user is authenticated
  isAuthenticated(): boolean {
    try {
      const authData = localStorage.getItem(AUTH_KEY);
      if (!authData) return false;

      const auth: AuthState = JSON.parse(authData);
      
      // Check if session has expired
      if (auth.lastLogin) {
        const lastLogin = new Date(auth.lastLogin).getTime();
        const now = new Date().getTime();
        
        if (now - lastLogin > SESSION_TIMEOUT) {
          this.logout();
          return false;
        }
      }

      return auth.isAuthenticated;
    } catch (error) {
      console.error('Auth check error:', error);
      return false;
    }
  }

  // Login with password
  login(password: string): boolean {
    if (password === ADMIN_PASSWORD) {
      const auth: AuthState = {
        isAuthenticated: true,
        lastLogin: new Date().toISOString(),
      };
      
      localStorage.setItem(AUTH_KEY, JSON.stringify(auth));
      return true;
    }
    return false;
  }

  // Logout
  logout(): void {
    localStorage.removeItem(AUTH_KEY);
  }

  // Get auth state
  getAuthState(): AuthState | null {
    try {
      const authData = localStorage.getItem(AUTH_KEY);
      return authData ? JSON.parse(authData) : null;
    } catch (error) {
      console.error('Get auth state error:', error);
      return null;
    }
  }

  // Update last login time
  updateLastLogin(): void {
    try {
      const auth = this.getAuthState();
      if (auth) {
        auth.lastLogin = new Date().toISOString();
        localStorage.setItem(AUTH_KEY, JSON.stringify(auth));
      }
    } catch (error) {
      console.error('Update last login error:', error);
    }
  }

  // Check if session is about to expire (within 1 hour)
  isSessionExpiringSoon(): boolean {
    try {
      const auth = this.getAuthState();
      if (!auth || !auth.lastLogin) return false;

      const lastLogin = new Date(auth.lastLogin).getTime();
      const now = new Date().getTime();
      const timeUntilExpiry = SESSION_TIMEOUT - (now - lastLogin);
      
      return timeUntilExpiry < 60 * 60 * 1000; // Less than 1 hour
    } catch (error) {
      console.error('Session expiry check error:', error);
      return false;
    }
  }

  // Get remaining session time in minutes
  getRemainingSessionTime(): number {
    try {
      const auth = this.getAuthState();
      if (!auth || !auth.lastLogin) return 0;

      const lastLogin = new Date(auth.lastLogin).getTime();
      const now = new Date().getTime();
      const timeUntilExpiry = SESSION_TIMEOUT - (now - lastLogin);
      
      return Math.max(0, Math.floor(timeUntilExpiry / (60 * 1000)));
    } catch (error) {
      console.error('Get remaining session time error:', error);
      return 0;
    }
  }
}

export const authService = new AuthService(); 